import type { KeywordTracker } from "@repo/app-types/entities/keyword";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { CreatableComboboxField } from "@repo/ui/combobox/creatableComboboxField";
import { type FC, useState } from "react";
import { useAppDispatch } from "../../hooks/hook.js";
import { updateKeywordTracker } from "../keywordTrackers/keywordTrackersSlice.js";
import {
  NotificationType,
  showNotification,
} from "../notificationsOverlay/notificationsSlice.js";

const KEYWORD_LENGTH_LIMIT = 50;

interface KeywordTrackerEditorProps {
  keywordTracker: KeywordTracker;
}

export const KeywordTrackerEditor: FC<KeywordTrackerEditorProps> = ({
  keywordTracker,
}) => {
  const dispatch = useAppDispatch();
  const [hasMadeChanges, setHasMadeChanges] = useState(false);

  const [keywordPhrases, setKeywordPhrases] = useState<string[]>(
    keywordTracker.keywords.map((keyword) => keyword.phrase),
  );

  const onSaveChanges = () => {
    dispatch(
      updateKeywordTracker({
        id: keywordTracker.id,
        changes: {
          keywords: keywordPhrases.map((phrase) => {
            const existingKeyword = keywordTracker.keywords.find(
              (k) => k.phrase === phrase,
            );
            if (existingKeyword) return existingKeyword;

            return {
              phrase,
              transcript_fragment_ids: [],
              analyses: [],
            };
          }),
        },
      }),
    );
    showNotification(dispatch, {
      id: `keyword-tracker-updated-${new Date().getTime()}`,
      primaryMessage: `Keyword tracker updated`,
      type: NotificationType.SUCCESS,
    });
    setHasMadeChanges(false);
  };

  return (
    <>
      {/* TODO: Make this copy generic when we support multiple keyword trackers */}
      <CreatableComboboxField
        label={keywordTracker.name}
        description="Add the names of your competitors so you can track their mentions in interviews. Matching is case-insensitive."
        placeholder="Add the name of a competitor"
        value={keywordPhrases}
        onChange={(phrases) => {
          // Rejecting a value means leaving it out of the next value; here
          // nothing is kept, so an over-long keyword is simply not added.
          const hasInvalidPhrase = phrases.some(
            (phrase) => phrase.length > KEYWORD_LENGTH_LIMIT,
          );

          if (hasInvalidPhrase)
            return showNotification(dispatch, {
              id: `keyword-too-long-${new Date().getTime()}`,
              primaryMessage: `Keywords must be less than 100 characters`,
              type: NotificationType.FAILURE,
            });

          setHasMadeChanges(true);
          setKeywordPhrases(phrases);
        }}
      />

      {hasMadeChanges ? (
        <div className="mt-3 flex space-x-2">
          <Button
            onClick={() => {
              setKeywordPhrases(keywordTracker.keywords.map((k) => k.phrase));
              setHasMadeChanges(false);
            }}
            variant={ButtonVariant.Secondary}
          >
            Cancel
          </Button>
          <Button onClick={onSaveChanges} variant={ButtonVariant.Primary}>
            Save changes
          </Button>
        </div>
      ) : null}
    </>
  );
};
